import { ArrowRight, Mail } from "lucide-react";

const CONTACT_EMAIL = "Valkyrie@valkyriestrategies.com";

const REASONS = [
    {
        title: "Press / Media",
        text: "Interviews, screeners, press kits, and coverage of the film or campaign.",
    },
    {
        title: "Investors / Producers",
        text: "Production partnerships, financing conversations, and distribution inquiries.",
    },
    {
        title: "Locations",
        text: "Offer a filming location — offices, halls, homes, or civic spaces.",
    },
    {
        title: "Crew / Volunteers",
        text: "Join the production team behind the camera in any capacity.",
    },
    {
        title: "General Questions",
        text: "Anything else — we read every message and reply within 48–72 hours.",
    },
];

export default function Contact() {
    return (
        <div data-testid="contact-page" className="pt-32 pb-20">
            <section className="max-w-7xl mx-auto px-6 sm:px-8 mb-16">
                <p className="font-mono text-base uppercase tracking-[0.3em] text-neutral-500 mb-6">
                    File 04 / Establish Contact
                </p>
                <h1
                    data-testid="contact-title"
                    className="font-display text-6xl sm:text-8xl lg:text-9xl uppercase leading-[0.95] tracking-tighter text-neutral-900 text-balance"
                >
                    Send a
                    <br />
                    <span className="italic text-blood font-light">Secure Line.</span>
                </h1>
                <p className="font-mono text-xl sm:text-2xl text-neutral-600 mt-8 max-w-3xl leading-relaxed">
                    Press, partnerships, locations, crew, or just a question — drop us a
                    line and we&apos;ll get back to you.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <aside className="lg:col-span-4 space-y-6">
                    <div className="border border-neutral-300 bg-ink2 p-8">
                        <Mail size={28} className="text-blood mb-6" />
                        <p className="font-accent uppercase tracking-[0.25em] text-base text-blood mb-3">
                            / Direct Channel
                        </p>
                        <p className="font-mono text-lg text-neutral-600 leading-relaxed">
                            Use this channel for any inquiry. For donations, please use the
                            secure GiveSendGo campaign page directly.
                        </p>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="contact-email-link"
                            className="inline-block font-mono text-lg text-blood hover:text-bloodHover transition-colors mt-4 break-all"
                        >
                            {CONTACT_EMAIL}
                        </a>
                    </div>
                    <InfoBlock label="Production" value="State of Deception, LLC" />
                    <InfoBlock label="Creator" value="Jason Lewis — Writer / Producer" />
                    <InfoBlock label="Status" value="Pre-production / Funding" />
                    <InfoBlock label="Response Time" value="48–72 hours" />
                </aside>

                <div className="lg:col-span-8">
                    <div
                        data-testid="contact-reasons"
                        className="border border-neutral-300 bg-ink2 p-8 sm:p-10"
                    >
                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-8">
                            / Why People Reach Out
                        </p>
                        <ul className="space-y-8">
                            {REASONS.map((r) => (
                                <li key={r.title} className="flex gap-5 items-start">
                                    <span className="w-2 h-2 bg-blood mt-3 shrink-0" />
                                    <div>
                                        <h3 className="font-accent uppercase tracking-[0.2em] text-xl text-neutral-900">
                                            {r.title}
                                        </h3>
                                        <p className="font-mono text-lg text-neutral-600 mt-1 leading-relaxed">
                                            {r.text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="contact-email-btn"
                            className="mt-12 w-full inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-bloodHover transition-colors border border-blood"
                        >
                            <Mail size={18} />
                            Contact Us <ArrowRight size={18} />
                        </a>
                        <p className="font-mono text-base text-neutral-500 mt-4 text-center">
                            Opens your email app addressed to{" "}
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="text-blood hover:text-bloodHover transition-colors break-all"
                            >
                                {CONTACT_EMAIL}
                            </a>
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}

const InfoBlock = ({ label, value }) => (
    <div className="border border-neutral-300 bg-ink p-6">
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-neutral-500 mb-2">
            {label}
        </p>
        <p className="font-display text-3xl text-neutral-900">{value}</p>
    </div>
);
